import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Code, Rocket, Zap } from 'lucide-react';

type StatConfig = {
  icon: typeof Code;
  value: number;
  suffix?: string;
  label: string;
  highlight?: boolean;
};

const useCountUp = (endValue: number, duration = 2000) => {
  const [currentValue, setCurrentValue] = useState(0);
  const [isCounting, setIsCounting] = useState(true);

  useEffect(() => {
    let animationFrame: number;
    let startTimestamp: number | null = null;

    const step = (timestamp: number) => {
      if (!startTimestamp) startTimestamp = timestamp;
      const progress = Math.min((timestamp - startTimestamp) / duration, 1);
      setCurrentValue(Math.floor(progress * endValue));

      if (progress < 1) {
        animationFrame = requestAnimationFrame(step);
      } else {
        setCurrentValue(endValue);
        setIsCounting(false);
      }
    };

    animationFrame = requestAnimationFrame(step);

    return () => cancelAnimationFrame(animationFrame);
  }, [endValue, duration]);

  return { currentValue, isCounting };
};

const StatCard = ({ icon: Icon, value, suffix = '+', label, highlight = false }: StatConfig) => {
  const { currentValue, isCounting } = useCountUp(value, 2200);

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
      className="bg-white/5 backdrop-blur-sm rounded-xl p-6 border border-white/10"
      aria-live="polite"
    >
      <motion.div
        animate={highlight && isCounting ? { y: [0, -10, 0] } : { y: 0 }}
        transition={{ duration: 0.8, repeat: highlight && isCounting ? Infinity : 0, ease: 'easeInOut' }}
        className="flex justify-center"
      >
        <Icon className="text-primary-300 mb-3" size={32} />
      </motion.div>
      <div className="text-4xl font-bold text-white mb-2">
        {currentValue}
        {suffix}
      </div>
      <div className="text-gray-300 text-sm">{label}</div>
    </motion.div>
  );
};

const HeroSection = () => {
  const scrollToContact = () => {
    const element = document.getElementById('contato');
    element?.scrollIntoView({ behavior: 'smooth' });
  };

  const fadeInUp = {
    initial: { opacity: 0, y: 30 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6 }
  };

  return (
    <section id="home" className="relative min-h-screen flex items-center justify-center overflow-hidden">
      <div className="absolute inset-0 z-0">
        <div className="relative w-full h-full">
          <div
            className="absolute inset-0 bg-cover bg-center"
            style={{
              backgroundImage: 'url(https://thumbs.dreamstime.com/b/abstract-blue-purple-digital-wave-landscape-glowing-flowing-lines-forming-futuristic-energy-surface-dark-background-smooth-444011667.jpg)',
            }}
          />
          <div className="absolute inset-0 bg-gradient-to-br from-primary-900/90 via-secondary-900/85 to-gray-900/90" />
        </div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32 text-center">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8 }}
          className="space-y-8"
        >
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="inline-flex items-center space-x-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full border border-white/20"
          >
            <Zap className="text-yellow-400" size={16} />
            <span className="text-white text-sm font-medium">Soluções Inovadoras em Desenvolvimento</span>
          </motion.div>

          <motion.h1
            {...fadeInUp}
            className="text-5xl md:text-6xl lg:text-7xl font-bold text-white leading-tight"
          >
            Transforme suas ideias em{' '}
            <span className="block mt-2">
              <strong className="text-transparent bg-clip-text bg-gradient-to-r from-primary-300 to-secondary-300">
                realidade digital
              </strong>
            </span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4, duration: 0.6 }}
            className="text-xl md:text-2xl text-gray-200 max-w-3xl mx-auto"
          >
            Desenvolvimento web de alta qualidade, consultoria especializada e soluções empresariais sob medida para o crescimento do seu negócio.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6, duration: 0.6 }}
            className="flex flex-col sm:flex-row items-center justify-center gap-4 pt-8"
          >
            <button
              onClick={scrollToContact}
              className="group bg-gradient-to-r from-primary-600 to-secondary-600 text-white px-8 py-4 rounded-lg font-semibold text-lg shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105 flex items-center space-x-2"
            >
              <span>Iniciar Projeto</span>
              <ArrowRight className="group-hover:translate-x-1 transition-transform" size={20} />
            </button>
            <button
              onClick={() => document.getElementById('portfolio')?.scrollIntoView({ behavior: 'smooth' })}
              className="bg-white/10 backdrop-blur-sm text-white border-2 border-white/30 px-8 py-4 rounded-lg font-semibold text-lg hover:bg-white/20 transition-all duration-300 flex items-center space-x-2"
            >
              <span>Ver Portfólio</span>
            </button>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8, duration: 0.6 }}
            className="grid grid-cols-1 sm:grid-cols-3 gap-8 pt-16 max-w-4xl mx-auto"
          >
            {[
              { icon: Code, value: 50, label: 'Projetos Entregues' },
              { icon: Rocket, value: 98, suffix: '%', label: 'Satisfação', highlight: true },
              { icon: Zap, value: 5, label: 'Anos de Experiência' },
            ].map((stat) => (
              <StatCard key={stat.label} {...stat} />
            ))}
          </motion.div>
        </motion.div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.2 }}
        className="absolute bottom-8 left-1/2 transform -translate-x-1/2"
      >
        <div className="w-6 h-10 border-2 border-white/30 rounded-full flex items-start justify-center p-2">
          <motion.div
            animate={{ y: [0, 12, 0] }}
            transition={{ duration: 1.5, repeat: Infinity }}
            className="w-1.5 h-1.5 bg-white rounded-full"
          />
        </div>
      </motion.div>
    </section>
  );
};

export default HeroSection;
